import { sharedSchema } from "@shared-database/pgSchema";
import { CommunityKindEnumValues } from "@shared-database/enums";
import { KnowledgeGraph } from "@shared-database/models/rag/knowledge-graph";
import { leoEnum, leoID, timestampColumns } from "@shared-database/utils";
import { sql, type InferInsertModel, type InferSelectModel } from "drizzle-orm";
import {
  type AnyPgColumn,
  index,
  integer,
  real,
  text,
  uniqueIndex,
  vector,
} from "drizzle-orm/pg-core";

import { EMBEDDING_DIMENSIONS } from "@shared/utils/rag/constants/extraction";
import { leoNotNullForeignID } from "@shared-database/utils";

export const communityKindEnum = leoEnum(
  "CommunityKind",
  CommunityKindEnumValues,
);

/**
 * Knowledge-graph-wide communities detected via Leiden across the full entity
 * graph. Member entities live in `KnowledgeCommunityMember`; per-report
 * findings live in `KnowledgeCommunityFinding`. Communities are only
 * surfaced when the caller can access at least one member via
 * `KnowledgeEntitySource`. `rating`, `ratingExplanation`, and the joined
 * findings follow GraphRAG's community-report shape so global search can
 * cite individual findings as well as the summary.
 */
export const KnowledgeCommunity = sharedSchema.table(
  "KnowledgeCommunity",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),
    knowledgeGraphId: leoNotNullForeignID("knowledgeGraphId").references(
      () => KnowledgeGraph.id,
      { onDelete: "cascade" },
    ),

    /**
     * Deterministic fingerprint derived from the community's sorted member
     * entity ids and its level (e.g. `sha256("{level}:id1,id2,...")`). Allows
     * profile community detection runs to upsert the same community row across runs
     * instead of churning primary keys every time, which matters for citation
     * stability and incremental UI caching.
     */
    stableKey: text("stableKey").notNull(),

    level: integer("level").notNull().default(0),
    title: text("title").notNull(),
    summary: text("summary").notNull(),

    /** 0-10 impact rating; higher = more salient / reasoning-worthy. */
    rating: real("rating").notNull().default(0),
    /** One-sentence justification for the rating (LLM-written). */
    ratingExplanation: text("ratingExplanation").notNull().default(""),

    /**
     * 2-4 LLM-drafted follow-up questions the community can answer,
     * surfaced as conversation starters in the knowledge-graph UI. The
     * summarisation prompt is zod-validated to return this range, so the
     * column is always populated. Stored as a native text[] rather than
     * jsonb — the values carry no structure and text[] matches the rest
     * of the schema (e.g. `KnowledgeEntity.types`).
     */
    suggestedQuestions: text("suggestedQuestions")
      .array()
      .notNull()
      .default(sql`ARRAY[]::text[]`),

    /**
     * Discriminates synthetic "tail residual" communities from organic
     * Leiden clusters. A residual stitches together every cluster beyond
     * `MAX_COMMUNITIES_TO_SUMMARIZE` into one bucket so the UI has
     * something to show for the long tail without paying an LLM call per
     * cluster. No default on purpose: detection knows which bucket it is
     * writing, and forcing an explicit value keeps the classifier honest.
     */
    kind: communityKindEnum("kind").notNull(),

    /**
     * Stable seed for palette-slot assignment in the UI. Derived from
     * the top-K sorted member entity ids at INSERT time and *never*
     * rewritten on update, so the community's swatch survives re-id
     * churn from detection reruns.
     */
    colorSeed: text("colorSeed").notNull(),

    // NOTE: an HNSW cosine index on `embedding`
    // (`KnowledgeCommunity_embedding_hnsw_idx`) exists in the hand-written
    // migration; drizzle-kit has no `hnsw` builder, so we deliberately
    // don't declare it here.
    embedding: vector("embedding", { dimensions: EMBEDDING_DIMENSIONS }),

    // References
    /**
     * Level-0 children point at their level-1 parent. Lets global search
     * promote a shortlist to the super-community summary when every hit
     * shares a parent, giving the LLM the more abstract (and cheaper)
     * answer for cross-cluster questions. Null on level-1 rows and on
     * level-0 rows that never rolled up.
     */
    parentCommunityId: text("parentCommunityId").references(
      (): AnyPgColumn => KnowledgeCommunity.id,
      { onDelete: "set null" },
    ),
  },
  (table) => [
    index("KnowledgeCommunity_knowledgeGraphIdx").on(table.knowledgeGraphId),
    index("KnowledgeCommunity_levelIdx").on(table.level),
    // Every retrieval path filters by (knowledgeGraphId, level) together
    // (see searchCommunities); a composite index beats the per-column
    // indexes for the selective common case.
    index("KnowledgeCommunity_knowledgeGraphLevelIdx").on(
      table.knowledgeGraphId,
      table.level,
    ),
    index("KnowledgeCommunity_parentIdx").on(table.parentCommunityId),
    uniqueIndex("KnowledgeCommunity_stableKeyUniqueIdx").on(
      table.knowledgeGraphId,
      table.stableKey,
    ),
  ],
);

// Types
export type KnowledgeCommunityType = InferSelectModel<
  typeof KnowledgeCommunity
>;
export type KnowledgeCommunityInsertType = InferInsertModel<
  typeof KnowledgeCommunity
>;
